import * as Sentry from '@sentry/browser';
import { filter, find, intersection, union, keyBy } from 'lodash';
import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { metricsDetails } from '@/constants';
import { DeviceType, compressorTypes } from '@/constants/deviceTypeEnum';
import { MetricType } from '@/constants/metric-type.enum';
import epaxios from '@/utils/epaxios';

import { CreateImagePayload } from '../SiteSurveyForm/constants';
import RootStore from '../RootStore';
import { Device } from '../Station/constants';
import { MAX_IMAGE_UPLOAD_COUNT } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/ImageUpload.consts';

export default class DevicesStore {
  rootStore: typeof RootStore;
  @observable devicesMetadata = [];
  @observable isLoading = false;
  @observable isReady = false;
  @observable pressureDropDevices = [];
  @observable deviceImages = [];
  @observable isLoadingDeviceImages = false;
  @observable isSavingDeviceImages = false;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    reaction(
      () => this.rootStore.stationStore?.currentStation?.id,
      async id => {
        if (!id) return;

        await this.rootStore.devicesStore.getDevicesMetadata();
      },
    );
  }

  @action getDevicesMetadata = async () => {
    try {
      this.isReady = false;
      this.devicesMetadata = [];
      this.isLoading = true;
      const { data } = await epaxios.get(`/station/${this.rootStore.stationStore.currentStation.id}/devices`);
      this.devicesMetadata = Array.isArray(data) ? data : [];
      this.isReady = true;
    } catch (e) {
      Sentry.captureMessage('Devices load error: ' + e);
      this.devicesMetadata = [];
    } finally {
      this.isLoading = false;
    }
  };

  @action getPressureDropDevices = async () => {
    try {
      this.pressureDropDevices = [];
      const { data } = await epaxios.get(
        `/station/${this.rootStore.stationStore.currentStation.id}/pressureDropDevices`,
      );
      // TODO check when an empty string is returned instead of empty array
      this.pressureDropDevices = Array.isArray(data) ? data : [];
    } catch (e) {
      Sentry.captureMessage('pressureDropDevices load error: ' + e);
      this.pressureDropDevices = [];
    }
  };

  @action getParentDeviceById = id => {
    const found = find(this.devicesMetadata, { id });
    return find(this.devicesMetadata, { id: found?.parentDeviceId });
  };

  getDeviceById = id => {
    return find(this.devicesMetadata, { id });
  };

  // The function returns the childId parent's name if parent exists and the child name if parent does not exist. Otherwise null
  getChildOrParentDeviceName = childId => {
    let name = null;
    const device = find(this.devicesMetadata, { id: childId });
    if (device && device.parentDeviceId) {
      const parent = find(this.devicesMetadata, { id: device.parentDeviceId });
      if (parent) {
        name = parent.name;
      }
    } else if (device) {
      name = device.name;
    }

    return name;
  };

  @computed get devicesMetadataWithoutPipes() {
    if (!Array.isArray(this.devicesMetadata)) return [];
    return filter(this.devicesMetadata, ({ deviceTypes }) => deviceTypes && !deviceTypes.includes(DeviceType.Pipe));
  }

  @computed get devicesMetadataWithoutPipesAndChildDevices() {
    return filter(this.devicesMetadataWithoutPipes, device => !device.parentDeviceId);
  }

  @computed get metricsKeys() {
    return this.devicesMetadataWithoutPipes
      .reduce((acc, device) => {
        return union(acc, device.metrics);
      }, [])
      .sort();
  }

  @computed get metrics(): { [metric in MetricType]: Device[] } {
    return this.devicesMetadataWithoutPipes.reduce((acc, device) => {
      device.metrics.forEach(metric => {
        (acc[metric] = acc[metric] || []).push(device);
      });
      return acc;
    }, {});
  }

  @computed get sortedCompressors() {
    return this.compressors.sort((a, b) => a.elementIndex - b.elementIndex);
  }

  @computed get compressors() {
    return filter(this.devicesMetadata, device => intersection(compressorTypes, device.deviceTypes)?.length);
  }

  @computed get flowSensors() {
    return filter(
      this.devicesMetadata,
      device => intersection([DeviceType.FlowSensor, DeviceType.FlowAndPressureSensor], device.deviceTypes)?.length,
    );
  }

  @computed get pressureSensors() {
    return filter(
      this.devicesMetadata,
      device => intersection([DeviceType.PressureSensor, DeviceType.FlowAndPressureSensor], device.deviceTypes)?.length,
    );
  }

  deviceMetadataFromDeviceId(deviceId) {
    return find(this.devicesMetadata, { id: deviceId });
  }

  getMetricName = metric => {
    return metricsDetails[metric]?.text || metric;
  };

  isCompressor = device => !!intersection(compressorTypes, device.deviceTypes)?.length;

  @action async getDeviceImages(deviceId: string) {
    const { customerId, siteId, id } = this.rootStore.stationStore.currentStation;
    if (customerId && siteId && id) {
      const path = `info-collection/device-images/customer/${customerId}/site/${siteId}/station/${id}/device/${deviceId}`;
      try {
        this.isLoadingDeviceImages = true;

        const { data } = await epaxios.get(path);

        this.deviceImages = data ?? [];
        this.rootStore.imageUploadStore.setMaxFiles(MAX_IMAGE_UPLOAD_COUNT - this.deviceImages?.length);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        this.isLoadingDeviceImages = false;
      }
    }
  }

  @action resizeDeviceImagesBatch = async (_stationId: string, signedPutUrls) => {
    const payload = signedPutUrls.map(image => ({
      filename: image.filename,
    }));
    try {
      await epaxios.post('info-collection/device-images/resize-batch', payload);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  @action saveDeviceImages = async (deviceId: string, images: CreateImagePayload[]) => {
    const { customerId, siteId, id } = this.rootStore.stationStore.currentStation;

    if (!images?.length || !customerId || !siteId || !id) {
      return;
    }

    this.isSavingDeviceImages = true;

    try {
      const signedPutUrls = await epaxios.post<{ id: string; url: string; filename: string }[]>(
        `info-collection/device-images/generate-live-put-urls`,
        {
          customerId,
          siteId,
          stationId: id,
          deviceId,
          images: images.map(({ file, ...image }) => ({ id: image.id, tag: image.tag, originalFilename: file.name })),
        },
      );

      const imagesDict = keyBy(images, 'id');

      for (const image of signedPutUrls.data) {
        await fetch(image.url, {
          method: 'PUT',
          body: imagesDict[image.id]?.file,
          headers: {
            'Content-Type': imagesDict[image.id]?.file?.type,
          },
        });
      }

      await this.resizeDeviceImagesBatch(
        id,
        signedPutUrls.data?.map(dataItem => ({ filename: dataItem.filename })),
      );
      await this.getDeviceImages(deviceId);
      this.rootStore.stationReadinessScoreStore?.reloadStationScore();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      this.isSavingDeviceImages = false;
    }
  };

  @action deleteImage = async (encodedS3Key: string) => {
    try {
      await epaxios.delete(`info-collection/device-images/image/${encodedS3Key}`);

      this.rootStore.stationReadinessScoreStore?.reloadStationScore();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  @action changeTag = async (encodedS3Key: string, newTag: string) => {
    try {
      await epaxios.put(`info-collection/device-images/image/tag/${encodedS3Key}`, {
        newTag,
      });
      this.rootStore.stationReadinessScoreStore?.reloadStationScore();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  getDeviceAttributeField = (deviceId: string, field: string) => {
    return this.getDeviceById(deviceId)?.attributes?.[field];
  };

  getDeviceControllerId = (deviceId: string): string => {
    const device = this.getDeviceById(deviceId);
    const controllerId = device?.controllerId || device?.deviceModel?.deviceControllerId;
    return controllerId || '';
  };
}
